import { yupResolver } from '@hookform/resolvers/yup';
import ErrorIcon from '@mui/icons-material/Error';
import AlertMUI from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { Button, Input, NavHeader } from '../../components';
import { useAuth } from '../../context/auth';
import { ILogin } from '../../controller/UserController';
import { Body, Container, Content, Form } from './styles';
import { ProfileType, TutorialType } from '../../types/enums';
import { AppVersion } from '../../components/AppVersion';
import JoyrideTour from '../../components/JoyrideTour';
import TitlePage from '../../components/TitlePage';
import { useTutorialProgress } from '../../context/tutorialProgress';

export function Login() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { tutorialProgress } = useTutorialProgress();
  const [isVerified, setIsVerified] = useState(false);
  const [runningTutorial, setRunningTutorial] = useState(false);
  const [isTestEnvironment, setIsTestEnvironment] = useState(false);
  const [asError, setAsError] = useState(false);
  const [asErrorNotFound, setAsErrorFound] = useState(false);
  const [asErrorDisabled, setAsErrorDisabled] = useState(false);
  const [asErrorInAccountWithProvider, setAsErrorInAccountWithProvider] = useState(false);
  const [asErrorWrongTenant, setAsErrorWrongTenant] = useState(false);
  const [defaultValues] = useState({
    email: '',
    password: '',
  });
  const steps = [
    {
      target: '#start-item',
      content:
        'Esta página é destinada ao login na plataforma, preencha seu email e senha para entrar.',
      disableBeacon: true,
    },
    {
      target: '#email-input',
      content: 'Caso já possua uma conta cadastrada, insira seu e-mail no campo.',
    },
    {
      target: '#pass-input',
      content: 'Insira sua senha.',
    },

    {
      target: '#sign-in-button',
      content: 'Clique no botao de Entrar para acessar a plataforma.',
    },
    {
      target: '#forget-pass',
      content: 'Caso tenha esquecido sua senha, clique aqui para recuperá-la.',
    },
    {
      target: '#register-account',
      content: 'Caso não possua uma conta, clique aqui para se cadastrar.',
    },
    // {
    //   target: '#social-login',
    //   content: 'Tambem é possível fazer login com sua conta do Google, Linkedin ou Facebook.',
    // },
  ];

  const loginSchema = yup.object().shape({
    email: yup.string().required('O campo e-mail é obrigatório').email('Digite um e-mail válido'),
    password: yup
      .string()
      .required('O campo senha é obrigatório')
      .min(6, 'Senha deve ter no mínimo 6 dígitos'),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({ defaultValues, resolver: yupResolver(loginSchema) });

  const handleLogin = async (form: ILogin) => {
    setLoading(true);
    setAsError(false);
    setAsErrorFound(false);
    setAsErrorDisabled(false);
    login(form)
      .then((profileType) => {
        if (profileType === ProfileType.REQUESTER) {
          navigate('/collaborator/solicitation');
          return;
        }
        if (profileType === ProfileType.FINANCIAL) {
          navigate('/financial');
          return;
        }
        if (profileType !== undefined) {
          navigate('/yoursolicitations');
        }
        navigate('/feed');
      })
      .catch((error) => {
        const err = error.response.data.detail;
        if (err === 'WRONG_PASS') {
          setAsError(true);
        }
        if (err === 'NOT_FOUND') {
          setAsErrorFound(true);
        }
        if (err === 'DISABLED') {
          setAsErrorDisabled(true);
        }
        if (err === 'ACCOUNT_WITH_PROVIDER') {
          setAsErrorInAccountWithProvider(true);
        }
        if (err === 'WRONG_TENANT') {
          setAsErrorWrongTenant(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOnChange = (token: string | null) => {
    token != null ? setIsVerified(true) : setIsVerified(false);
  };

  useEffect(() => {
    const url = window.location.hostname;
    if (url.includes('local') || url.includes('atc.') || url.includes('homolog.')) {
      setIsTestEnvironment(true);
      setIsVerified(true);
    }
  }, []);

  useEffect(() => {
    if (!tutorialProgress?.includes(TutorialType.LOGIN)) {
      setRunningTutorial(true);
    }
  }, [tutorialProgress]);

  return (
    <Body>
      <JoyrideTour
        type={TutorialType.LOGIN}
        steps={steps}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <NavHeader typeHeader="default" />
      <Container>
        <div className="page-title">
          <TitlePage
            title="Login"
            onRequestOpen={() => {
              setRunningTutorial(true);
            }}
            id="start-item"
          />
        </div>
        <Content>
          <div className={asError ? 'divAlert' : 'displayNone'}>
            <AlertMUI severity="error" variant="outlined" iconMapping={{ error: <ErrorIcon /> }}>
              E-mail ou a senha inserida está incorreta.
              <a href="/recoveryPassword" className="forgetPassword">
                Esqueceu a senha?
              </a>
            </AlertMUI>
          </div>
          <div className={asErrorDisabled ? 'divAlert' : 'displayNone'}>
            <AlertMUI severity="error" variant="outlined" iconMapping={{ error: <ErrorIcon /> }}>
              Está conta está desativada.
              {/* <a href="/reactivateAccount" className="forgetPassword">
                Deseja reativar sua conta?
              </a> */}
            </AlertMUI>
          </div>
          <div className={asErrorNotFound ? 'divAlert' : 'displayNone'}>
            <AlertMUI severity="error" variant="outlined" iconMapping={{ error: <ErrorIcon /> }}>
              O email que você inseriu não está conectado a uma conta.
              <a href="/register" className="forgetPassword">
                Crie uma conta
              </a>
            </AlertMUI>
          </div>
          <div className={asErrorInAccountWithProvider ? 'divAlert' : 'displayNone'}>
            <AlertMUI severity="error" variant="outlined" iconMapping={{ error: <ErrorIcon /> }}>
              <div className="containerMessage">
                <p>O e-mail inserido já está vinculado a uma rede social</p>
                <p>Favor entrar com a rede social para continuar</p>
              </div>
            </AlertMUI>
          </div>
          {asErrorWrongTenant && (
            <div className="divAlert">
              <AlertMUI severity="error" variant="outlined" iconMapping={{ error: <ErrorIcon /> }}>
                <div className="containerMessage">
                  <h1>Acesso não permitido neste ambiente</h1>
                  <p>
                    A conta utilizada não está autorizada para este ambiente. Verifique se você está
                    acessando o endereço correto ou utilize uma conta vinculada ao ambiente atual.
                  </p>
                </div>
              </AlertMUI>
            </div>
          )}
          <Form onSubmit={handleSubmit(handleLogin)}>
            <div className="formDiv">
              <Input
                id="email-input"
                name="email"
                control={control}
                type="text"
                placeholder="E-mail"
                errorText={errors.email?.message}
                variant="standard"
                fullWidth
                error={!!errors.email}
              />

              <Input
                id="pass-input"
                type="password"
                control={control}
                placeholder="Senha"
                name="password"
                errorText={errors.password?.message}
                variant="standard"
                fullWidth
                error={!!errors.password}
              />
              <div className="divRecaptcha">
                {process.env.REACT_APP_RECAPTCHA && !isTestEnvironment && (
                  <div className="recaptcha">
                    <ReCAPTCHA
                      sitekey={process.env.REACT_APP_RECAPTCHA}
                      onChange={handleOnChange}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="buttonDiv">
              <Button
                id="sign-in-button"
                type="submit"
                text="Entrar"
                color="primary"
                textHoverColor="background_white"
                dimensions={{ width: 208, height: 36 }}
                disabled={!isVerified}
                loading={loading}
              />
            </div>

            <div className="forgetDiv">
              <span className="forgetPassword" id="forget-pass">
                Esqueceu a senha? <a href="/recoveryPassword">Clique aqui</a>
              </span>

              {/* <span className="forgetPassword" id="reactivate-account">
                Quer reativar sua conta? <a href="/reactivateAccount">Clique aqui</a>
              </span> */}

              <span className="forgetPassword" id="register-account">
                Não possui uma conta ainda? <a href="/register">Clique aqui</a>
              </span>
            </div>
            {/* <div id="social-login">
              <SocialLogin />
            </div> */}
          </Form>
        </Content>
      </Container>
      <AppVersion />
    </Body>
  );
}
